/**
 * CANONICAL TESLA FACTS — single source of truth.
 * ----------------------------------------------------------------------------
 * Every page, FAQ, JSON-LD blob, and install step that states a Tesla fact must
 * align to the values here. Do NOT hardcode a competing value elsewhere; import
 * from this file (or, for prose, match these values exactly). The anti-slop CI
 * check (scripts/qa/anti-slop-check.mjs) blocks known-bad variants like the
 * legacy `Boombox/Locked/` USB path.
 *
 * Why this exists: a May 2026 AI-slop audit found the codebase asserted
 * conflicting facts across faq-data.ts, schema.ts, model-data.ts, and the
 * install flows (wrong USB path, brittle firmware versions, contradictory model
 * support). Conflicting "facts" read as hallucinated slop and erode trust.
 *
 * Sources & confidence live in docs/CANONICAL_FACTS.md. Items marked
 * NEEDS-CONFIRMATION there must not be asserted as precise fact in UI copy.
 */

import { PUBLIC_SOUND_COUNT_LABEL } from './public-sound-count'

export const TESLA_FACTS = {
  /**
   * Where the lock-sound file goes on the USB drive.
   * The site standardizes on `Boombox/LockChime.wav`. There is definitively NO
   * `Locked/` subfolder — that was a bug. Owner-tested 2026-10-01: the file
   * also works at the USB ROOT, so root placement is a troubleshooting
   * fallback (`rootFallback`), never a "wrong" path. See
   * docs/CANONICAL_FACTS.md item 1 (RESOLVED).
   */
  usb: {
    /** Site-canonical path. There is NO `Locked/` subfolder. */
    path: 'Boombox/LockChime.wav',
    /** Also works (owner-tested 2026-10-01). Offer as a fallback, never as the primary instruction. */
    rootFallback: 'If the car does not pick the file up from Boombox/, try putting LockChime.wav at the top level of the drive instead. Both locations work.',
    folder: 'Boombox',
    filename: 'LockChime.wav',
    /** Both work; FAT32 is the most universally compatible. */
    formats: ['FAT32', 'exFAT'] as const,
    /**
     * Port wording for every model. docs/CANONICAL_FACTS.md item 1 supports only
     * this much: the port must carry data, rear ports are power-only, and on
     * cars built after about Nov 2021 the center-console ports are charge-only,
     * so the glovebox port is the one to use. The canon is silent on which port
     * each model has, so no per-model port is asserted.
     */
    portNote: 'a USB port that carries data (the glovebox port on newer cars; rear ports only charge)',
    portShort: 'Data port (glovebox on newer cars)',
  },

  /**
   * Audio spec. The ONLY hard Tesla requirement is: a WAV file named exactly
   * `LockChime.wav`. The numeric specs below are widely-used best practice for
   * clean playback — they are NOT published Tesla requirements, so describe them
   * as "recommended", never "Tesla requires".
   */
  audio: {
    requiredFormat: 'WAV',
    requiredFilename: 'LockChime.wav',
    /** Recommended (best-practice), not an official Tesla spec. */
    recommended: {
      sampleRate: '44.1 kHz',
      bitDepth: '16-bit',
      duration: '1–5 seconds',
      /**
       * Machine-readable ceiling for the duration guidance above, in seconds.
       * Exists because three places hardcoded their own number and disagreed:
       * the trimmer, the voice generator and lib/audio-utils all used 5 while
       * lib/sound-utils used 2 and told the user "Tesla allows up to 2s".
       * Both figures were also framed as a Tesla limit, which this file has
       * always denied: the duration line is RECOMMENDED, not a published
       * Tesla spec. Read this value, do not restate a number.
       */
      maxDurationSeconds: 5,
      maxFileSize: 'under 1 MB',
    },
    /**
     * Do NOT emit a precise bitrate as machine-readable fact for arbitrary
     * user-uploaded WAVs — it varies per file. Left undefined on purpose.
     */
    bitrate: undefined as string | undefined,
  },

  /**
   * Firmware. The custom LOCK SOUND feature shipped in Tesla's 2023 Holiday
   * Update (software 2023.44.x), December 2023 — distinct from Boombox itself,
   * which arrived in the 2020 Holiday Update. Verified against release notes
   * (see docs/CANONICAL_FACTS.md). Prefer the non-brittle `label` in UI copy.
   */
  firmware: {
    minimum: '2023.44',
    minimumLabel: 'software 2023.44 or later',
    /**
     * Exact version verified in the release coverage (docs/CANONICAL_FACTS.md
     * item 3, HIGH confidence: the lock sound shipped in 2023.44.25, rolled
     * wider in 2023.44.30.x). Use this wherever a precise version appears
     * (schema, HowTo, spec rows). Keep the short "2023.44" for human UI sentences.
     */
    exactVersion: '2023.44.25',
    featureSince: 'Tesla’s 2023 Holiday Update (software 2023.44)',
    /** Safe, accurate, model-agnostic sentence for FAQ/UI copy. */
    label:
      'Custom lock sounds have been available since Tesla’s 2023 Holiday Update (software 2023.44) on cars with the external speaker. If you don’t see the Lock Sound option, update your software from Controls → Software.',
  },

  /**
   * Model support. The real requirement is the external pedestrian-warning
   * speaker (PWS / AVAS). Year cutoffs below track when each line shipped it.
   * MEDIUM confidence on exact cutoffs (individual VINs vary).
   */
  support: {
    requirement: 'an external pedestrian-warning speaker',
    requirementShort: 'external speaker',
    models: {
      'model-3': { label: 'Model 3', supported: 'Built September 2019 or later' },
      'model-y': { label: 'Model Y', supported: 'All model years' },
      'model-s': { label: 'Model S', supported: 'The 2021 refresh or later' },
      'model-x': { label: 'Model X', supported: 'The 2021 refresh or later' },
      cybertruck: { label: 'Cybertruck', supported: 'All' },
    },
  },

  /**
   * The refreshed Model Y ("Juniper"). Build years differ by region, so name
   * it by model year, once, here. Custom lock sounds work on every Model Y
   * (see `support.models`); this only labels the refresh in port guidance.
   */
  juniper: {
    label: 'Juniper',
    years: '2025+',
  },

  /**
   * One compatibility statement, rendered everywhere a page says which cars
   * work. Built from `support` and `firmware` above; never hand-write a
   * variant ("works on every Tesla", "all 5 models", "2020–2026").
   */
  compatibility: {
    statement:
      'Custom lock sounds need the car’s external pedestrian-warning speaker (Model 3 built September 2019 or later, Model S and Model X from the 2021 refresh, every Model Y and Cybertruck) and software 2023.44 or later.',
    short: 'Needs the external speaker (Model 3 from Sept 2019, S/X from the 2021 refresh) and software 2023.44+',
    inCarCheck: 'Check first: does Toybox → Boombox → Lock Sound exist in your car?',
    inCarCheckHelp:
      'If it does, you are set. If it does not, update your software from Controls → Software, then look again. If it still is not there, your car likely predates the external speaker.',
  },

  /**
   * Touchscreen path. Sourced from Tesla's Owner's Manual (CANONICAL_FACTS
   * item 6). One string for the final step, everywhere.
   */
  menu: {
    path: 'Toybox → Boombox → Lock Sound',
    finalStep: 'Toybox → Boombox → Lock Sound, then choose USB',
  },

  /**
   * Licence. Mirrors the Terms of Service: personal use in your own car, clips
   * are third-party works, DMCA notices are honored. Do not describe sounds as
   * CC-licensed or "original".
   */
  license: {
    short: 'Personal use in your own car. Clips are third-party works. DMCA notices honored.',
    footer: 'Personal use in your own car · Clips are third-party works · DMCA honored',
  },

  /** Brand string. One spelling in prose; the domain stays lowercase. */
  brand: 'Tesla Lock Sound',

  /**
   * Install time. ONE number site-wide: the guide itself claims two minutes
   * ("USB stick. Two minutes."), so every other surface (nav pill, library
   * install-path panel, HowTo JSON-LD totalTime) must say the same. Never
   * hardcode "5 minutes" or "PT5M" for the install flow again.
   */
  install: {
    minutes: 2,
    /** Prose form: "2 minutes". */
    label: '2 minutes',
    /** ISO 8601 duration for schema.org HowTo.totalTime. */
    iso: 'PT2M',
  },

  /**
   * Library size. The single source of truth is PUBLIC_SOUND_COUNT_LABEL
   * (lib/public-sound-count.ts) — re-exported here so fact consumers have one
   * import. NEVER hardcode a count like "900+"/"950+"/"1,000+"; those are
   * banned stale strings (see the deploy-smoke checklist + stale-copy test).
   */
  soundCountFallback: PUBLIC_SOUND_COUNT_LABEL,

  /**
   * Paint Shop wrap PNGs. Sibling folder on the same USB as LockChime.wav.
   * Source: Tesla custom-wraps README (docs/CANONICAL_FACTS.md item 8).
   * tesla-wrap.design is an independent free browser studio, not a Tesla product.
   */
  wrap: {
    folder: 'Wraps',
    path: 'Wraps/',
    format: 'PNG',
    menu: 'Toybox → Paint Shop → Wraps',
    designer: {
      name: 'tesla-wrap.design',
      url: 'https://tesla-wrap.design',
      role: 'Paint Shop wrap designer',
    },
  },

  /** Canonical Tesla-facing source links. See docs/CANONICAL_FACTS.md for confidence notes. */
  sources: {
    ownersManual: 'https://www.tesla.com/ownersmanual',
    support: 'https://www.tesla.com/support',
    customWraps: 'https://github.com/teslamotors/custom-wraps',
  },
} as const

export type TeslaModelKey = keyof typeof TESLA_FACTS.support.models
